import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { fmtDate } from "@/lib/constants";
import { BP_EVIDENCE_TYPES, BP_OWNERS, BP_REVIEW_STATUSES, BP_REVIEW_STYLES, BP_DIMENSIONS, BP_TOOL_STATUSES, BP_CUSTOMER_SYSTEM_TYPES } from "@/lib/reconConstants";
import { BlueprintDetail } from "./BlueprintDetail";
import { ImportDialog } from "./ImportDialog";
import { Library, Plus, Upload, Download, Search, Layers, CheckCircle2, FileEdit, AlertTriangle, Archive, Ban, ShieldAlert, RefreshCw, Cloud } from "lucide-react";
import { RecordActionsMenu, RecordBadges } from "@/components/RecordActions";

const Badge = ({ className, children }) => (
  <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium border ${className}`}>{children}</span>
);

export default function BlueprintLibrary() {
  const [me, setMe] = useState(null);
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [refs, setRefs] = useState([]);
  const [guide, setGuide] = useState([]);
  const [filters, setFilters] = useState({ evidence_type: "all", expected_owner: "all", review_status: "all", active: "all", dimension: "all", customer_system_type: "all", tool_selection_status: "all", needs_vendor_review: "all" });
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [catalog, setCatalog] = useState(null);
  const caps = me?.caps || {};

  const load = useCallback(() => {
    const params = { q };
    Object.entries(filters).forEach(([k, v]) => { if (v !== "all") params[k] = (k === "needs_vendor_review" ? "yes" : v); });
    api.get("/blueprints", { params }).then((r) => setItems(r.data)).catch(() => {});
    api.get("/blueprints/stats").then((r) => setStats(r.data)).catch(() => {});
  }, [q, filters]);
  useEffect(() => { api.get("/recon/me").then((r) => setMe(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/blueprints/reference-documents").then((r) => setRefs(r.data)).catch(() => {});
    api.get("/blueprints/collection-guide").then((r) => setGuide(r.data)).catch(() => {});
    api.get("/blueprints/microsoft-catalog").then((r) => setCatalog(r.data)).catch(() => {});
  }, []);

  const runMigration = () => {
    api.post("/blueprints/migrate/vendor-neutral").then((r) => { toast.success(`Migration: ${r.data.migrated} processed, ${r.data.flagged_needs_review} flagged for review.`); load(); })
      .catch((e) => toast.error(e.response?.data?.detail || "Migration failed."));
  };

  const exportLib = async () => {
    try {
      const resp = await api.get("/blueprints/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement("a"); a.href = url; a.download = "evidence_blueprint_library.csv";
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };

  const cards = stats ? [
    { label: "Total Blueprints", value: stats.total, icon: Layers, tone: "navy" },
    { label: "Approved", value: stats.approved, icon: CheckCircle2, tone: "emerald" },
    { label: "Draft", value: stats.draft, icon: FileEdit, tone: "slate" },
    { label: "Needs Re-Review", value: stats.needs_re_review, icon: AlertTriangle, tone: "amber" },
    { label: "Vendor Review", value: stats.needs_vendor_review, icon: ShieldAlert, tone: "amber" },
    { label: "Controls w/o Blueprint", value: stats.controls_with_no_blueprint, icon: Ban, tone: "red" },
  ] : [];

  return (
    <div>
      <header className="bg-navy px-6 md:px-8 py-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="overline text-gold mb-1.5">Operations · Admin Only</p>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-white tracking-tight flex items-center gap-2">
            <Library className="h-6 w-6 text-gold" aria-hidden="true" /> Evidence Blueprint Library
          </h1>
          <p className="text-slate-300 text-sm mt-2 max-w-3xl">Reusable, neutralized evidence patterns that guide what to collect for each CMMC L2 control and assessment objective. No source organization identity is stored or shown.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {caps.create && <Button className="bg-gold text-navy hover:bg-gold-dark" onClick={() => setShowCreate(true)} data-testid="bp-new-btn"><Plus className="h-4 w-4 mr-1.5" /> New Blueprint</Button>}
          {caps.create && <Button variant="outline" className="bg-white/5 border-white/20 text-white hover:bg-white/10" onClick={() => setShowImport(true)} data-testid="bp-import-btn"><Upload className="h-4 w-4 mr-1.5" /> Import</Button>}
          {caps.create && <Button variant="outline" className="bg-white/5 border-white/20 text-white hover:bg-white/10" onClick={runMigration} data-testid="bp-migrate-btn"><RefreshCw className="h-4 w-4 mr-1.5" /> Vendor-Neutral Migrate</Button>}
          <Button variant="outline" className="bg-white/5 border-white/20 text-white hover:bg-white/10" onClick={exportLib} data-testid="bp-export-btn"><Download className="h-4 w-4 mr-1.5" /> Export</Button>
        </div>
      </header>

      <div className="p-6 md:p-8 space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="bp-stat-cards">
          {cards.map((c) => (
            <div key={c.label} className="rounded-md border border-border bg-card p-4">
              <div className="flex items-center gap-1.5 text-muted-foreground text-xs mb-1"><c.icon className="h-3.5 w-3.5" /> {c.label}</div>
              <div className="text-2xl font-bold text-navy">{c.value}</div>
            </div>
          ))}
        </div>

        <Tabs defaultValue="library">
          <TabsList data-testid="bp-tabs">
            <TabsTrigger value="library" data-testid="bp-tab-library">Blueprints</TabsTrigger>
            <TabsTrigger value="reference" data-testid="bp-tab-reference">Reference Documents</TabsTrigger>
            <TabsTrigger value="guide" data-testid="bp-tab-guide">Collection Guide</TabsTrigger>
            <TabsTrigger value="microsoft" data-testid="bp-tab-microsoft">Microsoft Catalog</TabsTrigger>
          </TabsList>

          <TabsContent value="library" className="space-y-4 mt-4">
            <div className="rounded-md border border-border bg-card p-4 flex flex-wrap items-end gap-3">
              <div className="flex-1 min-w-[220px] space-y-1.5"><Label className="text-xs">Search</Label>
                <div className="relative"><Search className="h-4 w-4 absolute left-2.5 top-2.5 text-muted-foreground" />
                  <Input className="pl-8" placeholder="Control, objective, technology, evidence, portal…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="bp-search" /></div>
              </div>
              <FilterSel label="Evidence type" value={filters.evidence_type} onChange={(v) => setFilters({ ...filters, evidence_type: v })} opts={BP_EVIDENCE_TYPES} testid="bp-filter-type" />
              <FilterSel label="Owner" value={filters.expected_owner} onChange={(v) => setFilters({ ...filters, expected_owner: v })} opts={BP_OWNERS} testid="bp-filter-owner" />
              <FilterSel label="Review status" value={filters.review_status} onChange={(v) => setFilters({ ...filters, review_status: v })} opts={BP_REVIEW_STATUSES} testid="bp-filter-status" />
              <FilterSel label="Dimension" value={filters.dimension} onChange={(v) => setFilters({ ...filters, dimension: v })} opts={BP_DIMENSIONS} testid="bp-filter-dim" />
              <FilterSel label="Customer system type" value={filters.customer_system_type} onChange={(v) => setFilters({ ...filters, customer_system_type: v })} opts={BP_CUSTOMER_SYSTEM_TYPES} testid="bp-filter-systype" />
              <FilterSel label="Tool selection" value={filters.tool_selection_status} onChange={(v) => setFilters({ ...filters, tool_selection_status: v })} opts={BP_TOOL_STATUSES} testid="bp-filter-tool" />
              <div className="space-y-1.5"><Label className="text-xs">Vendor review</Label>
                <Select value={filters.needs_vendor_review} onValueChange={(v) => setFilters({ ...filters, needs_vendor_review: v })}>
                  <SelectTrigger className="w-[130px]" data-testid="bp-filter-vendorreview"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="all">All</SelectItem><SelectItem value="yes">Needs review</SelectItem></SelectContent>
                </Select></div>
              <div className="space-y-1.5"><Label className="text-xs">Active</Label>
                <Select value={filters.active} onValueChange={(v) => setFilters({ ...filters, active: v })}>
                  <SelectTrigger className="w-[130px]" data-testid="bp-filter-active"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="all">All</SelectItem><SelectItem value="active">Active</SelectItem><SelectItem value="archived">Archived</SelectItem></SelectContent>
                </Select></div>
            </div>

            <div className="rounded-md border border-border bg-card overflow-hidden">
              <div className="overflow-x-auto"><table className="w-full text-sm">
                <thead><tr className="bg-slate-50 border-b border-border text-left">
                  {["ID", "Control", "Assessment Objective", "Title", "System / Portal", "Evidence Type", "Owner", "Review Status", "Ver", "Updated"].map((h) => <th key={h} className="px-3 py-3 font-semibold text-navy text-xs uppercase tracking-wider whitespace-nowrap">{h}</th>)}
                </tr></thead>
                <tbody>
                  {items.length === 0 ? <tr><td colSpan={10} className="px-4 py-12 text-center text-muted-foreground" data-testid="bp-empty">No blueprints match your filters.</td></tr>
                    : items.map((b) => (
                      <tr key={b.id} onClick={() => setSelected(b.id)} className="border-b border-border last:border-0 hover:bg-slate-50 cursor-pointer" data-testid={`bp-row-${b.blueprint_code}`}>
                        <td className="px-3 py-3 font-mono text-xs">{b.blueprint_code}</td>
                        <td className="px-3 py-3 font-mono text-xs whitespace-nowrap">{b.control_number}</td>
                        <td className="px-3 py-3 font-mono text-xs whitespace-nowrap">{b.assessment_objective}</td>
                        <td className="px-3 py-3 max-w-[240px]"><span className="line-clamp-1">{b.title}</span>{b.needs_manual_review && <Badge className="ml-1 bg-amber-100 text-amber-800 border-amber-200">review</Badge>}</td>
                        <td className="px-3 py-3 text-xs whitespace-nowrap">{b.system_or_portal || "—"}</td>
                        <td className="px-3 py-3 text-xs whitespace-nowrap">{b.evidence_type}</td>
                        <td className="px-3 py-3 text-xs whitespace-nowrap">{b.expected_owner}</td>
                        <td className="px-3 py-3"><Badge className={BP_REVIEW_STYLES[b.review_status]}>{b.review_status}</Badge></td>
                        <td className="px-3 py-3 text-xs">v{b.version}</td>
                        <td className="px-3 py-3 text-xs text-muted-foreground whitespace-nowrap" onClick={(e) => e.stopPropagation()}><div className="flex items-center gap-1">{fmtDate(b.updated_at)}<RecordBadges classification={b.classification} lifecycleState={b.lifecycle_state} /><RecordActionsMenu type="blueprint" id={b.id} onChanged={load} /></div></td>
                      </tr>
                    ))}
                </tbody>
              </table></div>
            </div>
          </TabsContent>

          <TabsContent value="reference" className="mt-4">
            <div className="rounded-md border border-border bg-card overflow-hidden">
              <div className="overflow-x-auto"><table className="w-full text-sm">
                <thead><tr className="bg-slate-50 border-b border-border text-left">{["ID", "Control", "Objective", "Neutral Display Name", "Type", "Review Status", "Conversion Notes"].map((h) => <th key={h} className="px-3 py-3 font-semibold text-navy text-xs uppercase tracking-wider">{h}</th>)}</tr></thead>
                <tbody>{refs.map((d) => (
                  <tr key={d.id} className="border-b border-border last:border-0" data-testid={`bp-ref-${d.doc_code}`}>
                    <td className="px-3 py-3 font-mono text-xs">{d.doc_code}</td>
                    <td className="px-3 py-3 font-mono text-xs">{d.control_number}</td>
                    <td className="px-3 py-3 font-mono text-xs">{d.assessment_objective}</td>
                    <td className="px-3 py-3">{d.display_name}</td>
                    <td className="px-3 py-3 text-xs">{d.document_type}</td>
                    <td className="px-3 py-3 text-xs">{d.review_status}</td>
                    <td className="px-3 py-3 text-xs text-muted-foreground max-w-[320px]"><span className="line-clamp-2">{d.conversion_notes}</span></td>
                  </tr>))}</tbody>
              </table></div>
            </div>
          </TabsContent>

          <TabsContent value="guide" className="mt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {guide.map((g) => (
                <div key={g.id} className="rounded-md border border-border bg-card p-4" data-testid={`bp-guide-${g.role}`}>
                  <div className="font-display font-bold text-navy mb-2">{g.role}</div>
                  <p className="text-xs text-muted-foreground mb-1"><span className="font-semibold text-navy">Provide: </span>{g.what_to_provide}</p>
                  <p className="text-xs text-muted-foreground mb-1"><span className="font-semibold text-navy">Must show: </span>{g.minimum_visible_details}</p>
                  <p className="text-xs text-red-700"><span className="font-semibold">Common rejection: </span>{g.common_rejection_reason}</p>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="microsoft" className="mt-4 space-y-4">
            {catalog && (
              <>
                <div className="rounded-md border-l-4 border-l-amber-400 bg-amber-50 p-3 text-xs text-amber-900" data-testid="bp-ms-warning">{catalog.workflow_warning}</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(catalog.catalog).map(([group, items]) => (
                    <div key={group} className="rounded-md border border-border bg-card p-4" data-testid={`bp-ms-group-${group.replace(/\s+/g, '-')}`}>
                      <div className="font-display font-bold text-navy text-sm mb-2 flex items-center gap-1.5"><Cloud className="h-4 w-4 text-blue-600" /> {group}</div>
                      <div className="flex flex-wrap gap-1.5">{items.map((i, ix) => <span key={ix} className="inline-flex items-center px-2 py-0.5 rounded text-[11px] bg-blue-50 text-blue-800 border border-blue-200">{i}</span>)}</div>
                    </div>
                  ))}
                </div>
                <div className="rounded-md border border-border bg-card p-4">
                  <div className="font-display font-bold text-navy text-sm mb-3">Lightweight Microsoft Operations Workflows</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {Object.entries(catalog.workflows).map(([name, w]) => (
                      <div key={name} className="rounded border border-border p-3" data-testid={`bp-ms-workflow-${name.replace(/\s+/g, '-')}`}>
                        <div className="font-semibold text-navy text-xs mb-1">{name}</div>
                        <div className="text-[11px] text-muted-foreground mb-1"><span className="font-medium">Components:</span> {w.components.join(", ")}</div>
                        <div className="text-[11px] text-muted-foreground"><span className="font-medium">Fields:</span> {w.fields.join(", ")}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </TabsContent>
        </Tabs>
      </div>

      <BlueprintDetail bid={selected} onClose={() => setSelected(null)} caps={caps} onChanged={load} />
      <ImportDialog open={showImport} onOpenChange={setShowImport} onImported={load} />
      <BlueprintDetail create={showCreate} onClose={() => setShowCreate(false)} caps={caps} onChanged={load} />
    </div>
  );
}

function FilterSel({ label, value, onChange, opts, testid }) {
  return (
    <div className="space-y-1.5"><Label className="text-xs">{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="w-[180px]" data-testid={testid}><SelectValue /></SelectTrigger>
        <SelectContent><SelectItem value="all">All</SelectItem>{opts.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
      </Select></div>
  );
}
